//@ts-nocheck

'use client'

import React, { forwardRef, useContext } from 'react'

import * as S from './Button.styles'

import Icon from '../Icon/Icon.component'
import LinkWrapper from '../LinkWrapper/LinkWrapper.component'
import { LineButton } from '../Lines/Lines.component'
import { SectionThemeContext } from '../../../contexts/SectionTheme.context'

import { ButtonLabel } from '../Typography/Typography.styles'
import { getAriaProps } from '@caffeina-siad/utils'
import colorScheme from '../../../theme/colorScheme'

type variants = 'primary' | 'secondary' | 'tertiary' | 'quaternary'

interface ButtonProps {
  children?: React.ReactNode;
  type?: 'button' | 'submit' | 'reset';
  href?: string;
  label?: string;
  typo?: string;
  variant?: variants;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  handleClick?: () => void;
  iconStart?: string;
  iconEnd?: string;
  iconStartSize?: number;
  iconEndSize?: number;
  useIconColor?: boolean;
  uppercase?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  innerRef?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  scrollTo?: string;
  inheritedColorScheme?: string;
  download?: string;
  target?: string;
  ariaLabel?: string;
  disabled?: boolean;
  tabIndex?: number;
}

const Button = ({
  children,
  type = 'button',
  href,
  label,
  typo = 'buttonMD',
  variant = 'primary',
  handleClick = () => {},
  iconStart,
  iconEnd,
  iconStartSize,
  iconEndSize,
  iconStartBackground,
  iconEndBackground,
  useIconColor = false,
  uppercase,
  handleOnKeyDown,
  innerRef,
  scrollTo,
  inheritedColorScheme = null,
  download,
  ...props
}: ButtonProps) => {
  // Button Variant
  const btnProps = Object.assign(
    {},
    label ? { $label: label } : null,
    iconStart ? { iconStart } : null,
    iconEnd ? { iconEnd } : null,
    variant ? { variant } : null
  )

  // Button label props
  const buttonLabelProps = Object.assign(
    {},
    typo ? { typo } : null,
    uppercase ? { uppercase } : null
  )

  // Get Aria props
  const ariaProps = getAriaProps({
    href,
    target: props.target,
    type,
    label,
    ariaLabel: props.ariaLabel,
    disabled: props.disabled,
    tabIndex: props.tabIndex,
    iconStart,
    iconEnd,
  })

  const sectionThemeContext = useContext(SectionThemeContext)
  const selectedColorScheme = sectionThemeContext ? sectionThemeContext.selectedColorScheme : 'green'

  const iconSizeDispatcher = {
    primary: 'medium',
    secondary: 'medium',
    tertiary: 'small',
  }

  const handleScrollTo = () => {
    const offset = 104
    const element = document.getElementById(scrollTo)

    if (!!element) {
      const y = element.getBoundingClientRect().top + window.scrollY - offset

      // Scroll to section
      window.scrollTo({ top: y, behavior: 'smooth' })
      // Set focus on first focusable element of current section
      const focusable = element.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (focusable && focusable.length > 0) {
        focusable[0].focus({ preventScroll: true })
      }
    }
  }

  return (
    <LinkWrapper href={href} target={props.target || '_self'} download={download}>
      <S.StyledButton
        {...props}
        {...btnProps}
        {...ariaProps}
        ref={innerRef}
        href={href}
        as={href ? 'a' : 'button'}
        onClick={scrollTo ? handleScrollTo : handleClick}
        colorScheme={inheritedColorScheme || selectedColorScheme}
        variant={variant}
        download={download}
      >
        {iconStart ? (
          <Icon
            name={iconStart}
            size={iconStartSize ? iconStartSize : iconSizeDispatcher[variant]}
            useIconColor={useIconColor}
            aria-hidden
          />
        ) : null}
        {label || children ? (
          <ButtonLabel {...buttonLabelProps} {...btnProps}>
            {label ? label : null}
            {children ? children : null}
          </ButtonLabel>
        ) : null}
        {iconEnd ? (
          <Icon
            name={iconEnd}
            size={iconEndSize ? iconEndSize : iconSizeDispatcher[variant]}
            useIconColor={useIconColor}
            aria-hidden
          />
        ) : null}
      </S.StyledButton>
    </LinkWrapper>
  )
}

export const ButtonForwardingRef = forwardRef((props, ref) => {
  return <Button {...props} innerRef={ref} />
})

Button.displayName = 'Button';

export default Button;